import { type QueryClient } from '@tanstack/react-query';
import type { QuerySessionResponse } from '../../contracts';
import {
  abortArtifactGeneration,
  beginArtifactGeneration,
  completeArtifactGeneration,
  resetArtifactGenerationCoordinatorForTests,
  stopAllArtifactGenerations,
} from './artifact-generation-coordinator';
import { markAbortAsSupersedingStream } from './generation-abort-reasons';

export type GenerationCancellationReason = 'user' | 'superseded' | 'cleanup' | 'recovery';

export type GenerationHandle = {
  sessionId: string;
  generationId: number;
  controller: AbortController;
};

type ActiveGeneration = GenerationHandle & {
  cancellationReason?: GenerationCancellationReason;
  postActionsCompleted: boolean;
};

let nextGenerationId = 0;
const activeGenerations = new Map<string, ActiveGeneration>();

export const beginSessionGeneration = ({
  queryClient,
  queryKey,
  sessionId,
  outgoingContent,
}: {
  queryClient: QueryClient;
  queryKey: string;
  sessionId: string;
  outgoingContent?: string | null;
}): GenerationHandle => {
  const previous = activeGenerations.get(sessionId);
  if (previous) previous.cancellationReason = 'superseded';

  const latestSession = queryClient.getQueryData<QuerySessionResponse>([
    queryKey,
    { sessionId },
  ]);
  const existingController = latestSession?.abortController;
  if (existingController && !existingController.signal.aborted) {
    markAbortAsSupersedingStream(existingController);
    existingController.abort();
  }

  const controller = new AbortController();
  queryClient.setQueryData<QuerySessionResponse>([queryKey, { sessionId }], (oldData) =>
    oldData ? { ...oldData, abortController: controller } : undefined
  );
  const handle: ActiveGeneration = {
    sessionId,
    generationId: ++nextGenerationId,
    controller,
    postActionsCompleted: false,
  };
  activeGenerations.set(sessionId, handle);
  beginArtifactGeneration({
    key: { kind: 'note', sessionId },
    outgoingContent:
      outgoingContent === undefined
        ? (latestSession?.consult_note?.result ?? null)
        : outgoingContent,
    controller,
  });
  return handle;
};

export const getCurrentSessionGeneration = (
  sessionId: string
): GenerationHandle | undefined => activeGenerations.get(sessionId);

/** Test compatibility for legacy action tests that did not carry a session. */
export const getOnlyActiveGenerationForTests = (): GenerationHandle | undefined =>
  activeGenerations.size === 1 ? activeGenerations.values().next().value : undefined;

export const isLatestGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean =>
  activeGenerations.get(handle.sessionId)?.generationId === handle.generationId;

export const isCurrentGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean => {
  const current = activeGenerations.get(handle.sessionId);
  return isLatestGeneration(handle) && !current?.controller.signal.aborted;
};

export const assertCurrentGeneration = (handle: GenerationHandle): void => {
  if (isCurrentGeneration(handle)) return;
  const error = new Error('Side-by-side generation is no longer current');
  error.name = 'AbortError';
  throw error;
};

export const stopSessionGeneration = (
  sessionId: string,
  reason: GenerationCancellationReason = 'user'
): boolean => {
  const current = activeGenerations.get(sessionId);
  if (!current) return false;
  current.cancellationReason = reason;
  if (!current.controller.signal.aborted) {
    current.controller.abort(reason);
  }
  abortArtifactGeneration({ kind: 'note', sessionId }, reason);
  return true;
};

export const stopAllSessionGenerations = (
  reason: GenerationCancellationReason = 'cleanup'
): void => {
  [...activeGenerations.values()].forEach((generation) => {
    generation.cancellationReason = reason;
    if (!generation.controller.signal.aborted) {
      generation.controller.abort(reason);
    }
    abortArtifactGeneration({ kind: 'note', sessionId: generation.sessionId }, reason);
  });
  activeGenerations.clear();
  stopAllArtifactGenerations(reason);
};

export const getGenerationCancellationReason = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): GenerationCancellationReason | undefined => {
  const current = activeGenerations.get(handle.sessionId);
  if (current?.generationId !== handle.generationId) return 'superseded';
  return current.cancellationReason;
};

export const claimGenerationPostActions = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean => {
  const current = activeGenerations.get(handle.sessionId);
  if (
    current?.generationId !== handle.generationId ||
    current.controller.signal.aborted ||
    current.postActionsCompleted
  ) {
    return false;
  }
  current.postActionsCompleted = true;
  return true;
};

export const finishSessionGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): void => {
  const current = activeGenerations.get(handle.sessionId);
  if (current?.generationId === handle.generationId) {
    completeArtifactGeneration({ kind: 'note', sessionId: handle.sessionId });
    activeGenerations.delete(handle.sessionId);
  }
};

/** Test-only registration without a React Query cache. */
export const registerSessionGenerationForTests = (
  sessionId: string,
  generationId = ++nextGenerationId
): GenerationHandle => {
  const controller = new AbortController();
  const handle: ActiveGeneration = {
    sessionId,
    generationId,
    controller,
    postActionsCompleted: false,
  };
  activeGenerations.set(sessionId, handle);
  nextGenerationId = Math.max(nextGenerationId, generationId);
  return handle;
};

/** Test-only reset for the module-level client generation registry. */
export const resetGenerationCoordinatorForTests = (): void => {
  activeGenerations.forEach((generation) => {
    if (!generation.controller.signal.aborted) generation.controller.abort('cleanup');
  });
  resetArtifactGenerationCoordinatorForTests();
  activeGenerations.clear();
  nextGenerationId = 0;
};
